import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Form, Input, Select } from 'antd'

interface Unit {
  unit_code: string;
  unit_label: string;
  value?: string;
  label?: string;
}

interface NodeData {
  en_id: string;
  en_node_name: string;
  en_ip_address: string;
  en_port: string;
  en_ae_title: string;
  en_location: string;
  en_operations: string;
  en_comments: string;
}

interface EditFormData {
  name: string;
  ip: string;
  port: string;
  aet: string;
  location: string;
  operations: string;
  comments: string;
}

interface EditNodeModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit?: (values: Partial<NodeData & { user_id: string }>) => Promise<void>;
  nodeData: NodeData | null;
  unitsList?: Unit[];
}

const EditNode: React.FC<EditNodeModalProps> = ({ visible, onCancel, onSubmit, nodeData, unitsList }) => {
  const [form] = Form.useForm();
  const [formChanges, setFormChanges] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (nodeData) {
      form.setFieldsValue({
        name: nodeData.en_node_name,
        ip: nodeData.en_ip_address,
        port: nodeData.en_port,
        aet: nodeData.en_ae_title,
        location: nodeData.en_location,
        operations: nodeData.en_operations,
        comments: nodeData.en_comments,
      });
    }
  }, [nodeData, form]);

  const unitsOptions = useMemo(() =>
    unitsList?.map(unit => ({
      label: unit.unit_label || unit.label || 'Unknown',
      value: unit.unit_code || unit.value || '',
    })).filter(option => option.value) || [], [unitsList]);

  useEffect(() => {
    if (!visible) {
      setSubmitting(false);
      setFormChanges({});
    }
  }, [visible]);

  const onFieldsChange = useCallback((changedFields: any[], allFields: any[]) => {
    if (!nodeData) return;

    const changes: Record<string, any> = {};
    changedFields.forEach(field => {
      const fieldName: string = field.name[0];
      const fieldValue: any = field.value;
      const originalFieldName = `en_${fieldName}` as keyof NodeData;

      if (fieldValue !== nodeData[originalFieldName]) {
        changes[fieldName] = fieldValue;
      }
    });
    setFormChanges(prev => ({ ...prev, ...changes }));
  }, [nodeData]);

  const handleOk = useCallback(async (): Promise<void> => {
    if (!nodeData) {
      console.error('No node data available for editing');
      return;
    }

    try {
      setSubmitting(true);

      const values: EditFormData = await form.validateFields();

      // Validate port if provided
      let portNumber: number | undefined;
      if (values.port) {
        portNumber = parseInt(values.port, 10);
        if (isNaN(portNumber) || portNumber < 1 || portNumber > 65535) {
          throw new Error('Please enter a valid port number (1-65535)');
        }
      }

      const changedValues: Partial<NodeData & { user_id: string }> = {
        ...formChanges,
        en_id: nodeData.en_id,
        ...(portNumber && { port: portNumber }),
      };

      await onSubmit?.(changedValues);
    } catch (error: any) {
      console.error('Error updating node:', error);
      if (error.errorFields) {
        // Form validation error - don't show message as Antd handles it
      } else {
        // Custom validation or API error
        console.warn('Validation or update failed:', error.message || error);
      }
    } finally {
      setSubmitting(false);
    }
  }, [form, formChanges, nodeData, onSubmit]);

  return (
    <Modal
      open={visible}
      title="Edit Node"
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
      width={680}
      className="mn-edit-modal"
      confirmLoading={submitting}
      okButtonProps={{ className: 'mn-primary-btn' }}
      okText={submitting ? 'Updating...' : 'Update'}
    >
      <Form
        layout="vertical"
        form={form}
        className="mn-form"
        onFieldsChange={onFieldsChange}
      >
        <div className='mn-form-grid'>
          <Form.Item
            label="Node Name"
            name="name"
            rules={[{ required: true, message: 'Please enter node name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Location"
            name="location"
          >
            <Select
              options={unitsOptions}
              placeholder="Select a location"
            />
          </Form.Item>

          <Form.Item
            label="IP / HostName"
            name="ip"
            rules={[
              { required: true, message: 'Please enter IP address' },
              {
                pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                message: 'Please enter a valid IP address (e.g., 192.168.1.1)'
              }
            ]}
          >
            <Input placeholder="e.g., 192.168.1.1" />
          </Form.Item>

          <Form.Item
            label="Port"
            name="port"
            rules={[
              { required: true, message: 'Please enter port' },
              {
                pattern: /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/,
                message: 'Please enter a valid port number (1-65535)'
              }
            ]}
          >
            <Input placeholder="e.g., 8080" type="number" min="1" max="65535" />
          </Form.Item>

          <Form.Item
            label="AE Title"
            name="aet"
            className="mn-span-2"
            rules={[
              { required: true, message: 'Please enter AE title' },
              {
                max: 16,
                message: 'AE Title must be 16 characters or less'
              },
              {
                pattern: /^[A-Za-z0-9_-]+$/,
                message: 'AE Title can only contain letters, numbers, underscores, and hyphens'
              }
            ]}
          >
            <Input placeholder="e.g., OHIF_NODE" maxLength={16} />
          </Form.Item>

          <Form.Item
            label="Comments"
            name="comments"
            className="mn-span-2"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default EditNode
